import { useState, useEffect, useRef, useCallback } from 'react';

export const useMouseHelper = () => {
  const [mouseState, setMouseState] = useState({
    mouseX: 0,
    mouseY: 0,
    realX: 0,
    realY: 0,
    target: null,
    lastTarget: null,
    action: '',
    lastAction: '',
    callback: '',
    axis: 'xy',
    delay: 8,
    smooth: true
  });

  const timeoutRef = useRef(null);
  const animationFrameRef = useRef(null);
  const mouseHelperRef = useRef(null);

  // Initialize mouse helper
  useEffect(() => {
    const mouseHelper = document.querySelector('.trx_addons_mouse_helper');
    mouseHelperRef.current = mouseHelper;

    if (mouseHelper) {
      // Start animation loop
      animationFrameRef.current = requestAnimationFrame(mouseMove);
    }

    // Global mousemove listener
    const handleGlobalMouseMove = (e) => {
      getMouseState(e);
    };

    document.addEventListener('mousemove', handleGlobalMouseMove);

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, []);

  const getMouseState = useCallback((e) => {
    if (e.clientX === undefined) return;

    const realX = e.clientX + (window.pageXOffset || document.documentElement.scrollLeft);
    const realY = e.clientY + (window.pageYOffset || document.documentElement.scrollTop);
    
    setMouseState(prev => ({
      ...prev,
      realX,
      realY,
      mouseX: e.clientX,
      mouseY: e.clientY
    }));
  }, []);

  const mouseMove = useCallback(() => {
    if (mouseHelperRef.current && mouseState.mouseX !== null) {
      const { mouseX, mouseY, delay } = mouseState;
      
      // Apply transform directly to the mouse helper
      mouseHelperRef.current.style.transform = `translate(${mouseX}px, ${mouseY}px)`;

      // Handle edge positioning classes
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      if (mouseX > windowWidth - 100) {
        mouseHelperRef.current.classList.add('trx_addons_mouse_helper_left');
      } else {
        mouseHelperRef.current.classList.remove('trx_addons_mouse_helper_left');
      }

      if (mouseY > windowHeight - 100) {
        mouseHelperRef.current.classList.add('trx_addons_mouse_helper_top');
      } else {
        mouseHelperRef.current.classList.remove('trx_addons_mouse_helper_top');
      }
    }

    animationFrameRef.current = requestAnimationFrame(mouseMove);
  }, [mouseState]);

  const resetMouseHelper = useCallback(() => {
    if (!mouseHelperRef.current) return;

    mouseHelperRef.current.classList.add('trx_addons_mouse_helper_reset');
    
    setTimeout(() => {
      if (mouseHelperRef.current) {
        mouseHelperRef.current.classList.remove(
          'trx_addons_mouse_helper_reset',
          'trx_addons_mouse_helper_active',
          'trx_addons_mouse_helper_hide'
        );

        // Remove action classes
        const classList = mouseHelperRef.current.classList;
        for (let className of classList) {
          if (className.startsWith('trx_addons_mouse_helper_action_')) {
            mouseHelperRef.current.classList.remove(className);
          }
        }
      }
    }, 300);
  }, []);

  const handleMouseEnter = useCallback((e) => {
    const element = e.currentTarget;
    
    // Get data attributes
    const action = element.dataset.mouseHelper || 'hover';
    const axis = element.dataset.mouseHelperAxis || 'xy';
    const delay = parseInt(element.dataset.mouseHelperDelay) || 8;
    const centered = element.dataset.mouseHelperCentered || '1';
    const bgColor = element.dataset.mouseHelperBgColor;
    const mode = element.dataset.mouseHelperMode;
    const text = element.dataset.mouseHelperText;
    const hideCursor = element.dataset.mouseHelperHideCursor;

    setMouseState(prev => ({
      ...prev,
      target: element,
      lastTarget: element,
      action,
      lastAction: action,
      axis,
      delay
    }));

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (mouseHelperRef.current && window.innerWidth >= 768) {
      // Add active and action classes
      mouseHelperRef.current.classList.add('trx_addons_mouse_helper_active', `trx_addons_mouse_helper_action_${action}`);
      
      // Handle centered class
      if (centered === '1') {
        mouseHelperRef.current.classList.add('trx_addons_mouse_helper_centered');
      } else {
        mouseHelperRef.current.classList.remove('trx_addons_mouse_helper_centered');
      }

      // Apply background color if specified
      if (bgColor) {
        mouseHelperRef.current.style.backgroundColor = bgColor;
      }

      // Apply blend mode if specified
      if (mode) {
        mouseHelperRef.current.style.mixBlendMode = mode;
      }

      // Hide cursor if specified
      if (hideCursor === '1') {
        document.body.style.cursor = 'none';
      }
    }
  }, []);

  const handleMouseLeave = useCallback((e) => {
    setMouseState(prev => ({
      ...prev,
      target: null,
      action: '',
      axis: 'xy',
      delay: 8
    }));

    if (mouseHelperRef.current) {
      mouseHelperRef.current.classList.remove(
        'trx_addons_mouse_helper_click'
      );

      // Remove action classes
      const classList = mouseHelperRef.current.classList;
      for (let className of classList) {
        if (className.startsWith('trx_addons_mouse_helper_action_')) {
          mouseHelperRef.current.classList.remove(className);
        }
      }

      // Reset cursor
      document.body.style.cursor = '';
    }

    timeoutRef.current = setTimeout(() => {
      resetMouseHelper();
    }, 10);
  }, [resetMouseHelper]);

  const handleMouseDown = useCallback((e) => {
    if (mouseHelperRef.current) {
      mouseHelperRef.current.classList.add('trx_addons_mouse_helper_click');
    }
  }, []);

  const handleMouseUp = useCallback((e) => {
    if (mouseHelperRef.current) {
      mouseHelperRef.current.classList.remove('trx_addons_mouse_helper_click');
    }
  }, []);

  return {
    mouseState,
    handleMouseEnter,
    handleMouseLeave,
    handleMouseDown,
    handleMouseUp
  };
};